import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useKaylas } from "@/lib/kaylasAuth";
import { MUM_HERO_LOGO } from "./assets";
import { Home, LogOut } from "lucide-react";
import "@/kaylas/theme.css";

const QUICK_TABS = [
  { to: "/kaylas-garden/tubers", label: "Tubers" },
  { to: "/kaylas-garden/blooms", label: "Blooms" },
  { to: "/kaylas-garden/flowers", label: "Flowers" },
  { to: "/kaylas-garden/seeds", label: "Seeds" },
  { to: "/kaylas-garden/journal", label: "Journal" },
  { to: "/kaylas-garden/database", label: "Database" },
  { to: "/kaylas-garden/gallery", label: "Gallery" },
  { to: "/kaylas-garden/summary", label: "Summary" },
  { to: "/kaylas-garden/wishlist", label: "Wishlist" },
  { to: "/kaylas-garden/tasks", label: "Tasks" },
  { to: "/kaylas-garden/builder", label: "Builder" },
];

export default function KaylasShell() {
  const { token, loading, logout, profile } = useKaylas();
  const nav = useNavigate();
  const loc = useLocation();
  if (loading) return <div className="kaylas-theme min-h-screen flex items-center justify-center font-display italic">Opening the garden gate…</div>;
  if (!token) return <Navigate to="/kaylas-garden/login" replace state={{ from: loc.pathname }} />;

  const onHome = loc.pathname === "/kaylas-garden/home" || loc.pathname === "/kaylas-garden";

  return (
    <div className="kaylas-theme min-h-screen pb-16" data-testid="mums-shell">
      <div className="mum-bg-layer" style={{ backgroundImage: 'url(/mums-shed/background.png)' }} aria-hidden="true" />
      {/* Top bar */}
      <header className="mum-topbar sticky top-0 z-30 backdrop-blur-sm bg-[var(--mum-paper)]/85 border-b border-[var(--mum-cornflower)] shadow-[0_4px_18px_-12px_rgba(15,27,60,0.5)]">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => nav("/kaylas-garden/home")}
            className="flex items-center gap-3 group"
            data-testid="mum-brand"
          >
            <img src={MUM_HERO_LOGO} alt="Kayla's Garden" className="w-11 h-11 rounded-full ring-1 ring-[var(--mum-cornflower)] mum-shimmer" />
            <div className="text-left leading-tight">
              <div className="kicker text-[0.7rem] tracking-[0.2em] text-[var(--mum-cobalt)] uppercase">Kayla's Secret Garden</div>
              <div className="font-display text-2xl text-[var(--mum-sapphire)]">Kayla&apos;s Garden</div>
            </div>
          </button>
          <div className="flex items-center gap-2">
            {!onHome && (
              <button type="button" onClick={() => nav("/kaylas-garden/home")} className="mum-btn-ghost" data-testid="mum-home-btn">
                <Home size={14} /> Home
              </button>
            )}
            {profile?.name && (
              <span className="hidden sm:inline mum-chip" data-testid="mum-welcome-name">Hi, Kayla</span>
            )}
            <button type="button" onClick={() => { logout(); nav("/"); }} className="mum-btn-ghost" data-testid="mum-logout">
              <LogOut size={14} /> Lock garden
            </button>
          </div>
        </div>
        {/* Slim breadcrumb tabs - hidden on home */}
        {!onHome && (
          <nav className="max-w-6xl mx-auto px-4 pb-3 mum-tabbar-mini" data-testid="mum-tabs">
            {QUICK_TABS.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) => `mum-tab ${isActive ? "is-active" : ""}`}
                data-testid={`mum-tab-${label.toLowerCase()}`}
              >
                {label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      <main className="max-w-6xl mx-auto px-3 sm:px-4 py-6">
        <div className="mum-stage">
          <div className="px-2 sm:px-4 py-2">
            <Outlet />
          </div>
        </div>
      </main>

      <footer className="text-center mt-8 text-xs italic font-display text-[var(--mum-cobalt)]/80">
        Kayla&apos;s Dahlias at Heritage · est. 1932
      </footer>
    </div>
  );
}
